// Phase 1 of the GPU raymarch renderer (#3 from the MagicaVoxel research): the
// DATA PATH the raymarch shader will read. MagicaVoxel stays responsive because
// editing only changes raw integers in a buffer — it never builds triangle
// geometry during a drag; a ray caster reads the new integers next frame. To do
// the same here we expose the work-volume voxels as a dense palette buffer (one
// byte per cell, destined for a WebGL R8 3D texture) plus the palette colours as
// an RGBA buffer (a 256x1 texture). The raymarch fragment shader (phase 2, which
// needs visual iteration) then DDA-traverses the 3D texture exactly like the CPU
// pick already does in voxel-pick.ts, so editing becomes a texture upload instead
// of a re-mesh.
//
// This module is intentionally pure + headless-testable: it builds the typed
// arrays and reports the dimensions; the Three.js texture/material wiring lands
// in phase 2 once we can iterate the shader against real pixels.

import { type PaletteLookup } from "./voxel-mesh";
import { type CellBounds } from "../interaction/pick";
import { CHUNK_AREA, CHUNK_SIZE, type VoxelGrid, parseChunkKey } from "../model/voxel-grid";

export interface VolumeData {
  // Palette index per cell, laid out for a WebGL 3D texture with a PADDED row
  // stride: index = x + texW*(y + h*z). texW = w rounded up to a multiple of 4 so
  // each R8 row is 4-byte aligned (a 3D texture of an odd width otherwise shears
  // diagonally — unpackAlignment=1 isn't honored for Data3DTexture). The padding
  // columns [w, texW) stay 0 and are never sampled (the DDA's bounds use w).
  data: Uint8Array;
  w: number;
  h: number;
  d: number;
  texW: number;
  // Cell-space origin (the min corner) so the shader maps texels ↔ world cells.
  ox: number;
  oy: number;
  oz: number;
}

const padW = (w: number): number => (w + 3) & ~3;

// Snapshot the work volume's occupancy as a dense palette buffer. Zero the
// buffer, then copy each occupied chunk's raw cells in directly — one Map lookup
// per CHUNK (in updateVolumeChunk), not per cell. The old per-cell grid.get did
// a chunk-key compute + Map lookup for every one of (up to) ~16.7M cells, which
// dominated Model-mode load + every full raymarch rebuild. Reusable buffer
// (`into`) avoids reallocating the (up to ~2 MB) array every frame.
export function buildVolumeData(
  grid: VoxelGrid,
  bounds: CellBounds,
  into?: Uint8Array,
): VolumeData {
  const w = Math.max(0, bounds.maxIx - bounds.minIx);
  const h = Math.max(0, bounds.maxIy - bounds.minIy);
  const d = Math.max(0, bounds.maxIz - bounds.minIz);
  const texW = padW(w);
  const n = texW * h * d;
  const data = into && into.length >= n ? into : new Uint8Array(n);
  data.fill(0, 0, n); // empty everywhere (incl. padding); occupied chunks overwrite
  const ox = bounds.minIx, oy = bounds.minIy, oz = bounds.minIz;
  const v: VolumeData = { data, w, h, d, texW, ox, oy, oz };
  // Only occupied chunks exist in the map; updateVolumeChunk skips any that fall
  // outside the volume (a phantom-chunk check), so this is O(occupied chunks).
  for (const [ck] of grid.rawChunks()) {
    const [cx, cy, cz] = parseChunkKey(ck);
    updateVolumeChunk(v, grid, cx, cy, cz);
  }
  return v;
}

// Refresh just one chunk's cells in an existing volume buffer — the incremental
// path. After an edit, only the dirty chunks change, so this re-reads ~4096
// cells per dirty chunk (direct from the chunk's array, one map lookup) instead
// of the whole volume. Cells outside the work volume are skipped.
export function updateVolumeChunk(
  v: VolumeData,
  grid: VoxelGrid,
  cx: number,
  cy: number,
  cz: number,
): boolean {
  const { data, w, h, d, texW, ox, oy, oz } = v;
  const bx = cx * CHUNK_SIZE, by = cy * CHUNK_SIZE, bz = cz * CHUNK_SIZE;
  // Phantom chunk (entirely outside the work volume — clearBox/fillBox mark a
  // 1-chunk border, much of it off-volume): skip without scanning 4096 cells.
  if (
    bx + CHUNK_SIZE <= ox || bx >= ox + w ||
    by + CHUNK_SIZE <= oy || by >= oy + h ||
    bz + CHUNK_SIZE <= oz || bz >= oz + d
  ) {
    return false;
  }
  const chunk = grid.getChunkStorage(cx, cy, cz);
  for (let lx = 0; lx < CHUNK_SIZE; lx++) {
    const x = bx + lx - ox;
    if (x < 0 || x >= w) continue;
    for (let ly = 0; ly < CHUNK_SIZE; ly++) {
      const y = by + ly - oy;
      if (y < 0 || y >= h) continue;
      const rowBase = lx * CHUNK_AREA + ly * CHUNK_SIZE;
      for (let lz = 0; lz < CHUNK_SIZE; lz++) {
        const z = bz + lz - oz;
        if (z < 0 || z >= d) continue;
        data[x + texW * (y + h * z)] = chunk ? chunk.cells[rowBase + lz] : 0;
      }
    }
  }
  return true;
}

// Per-CHUNK occupancy (255 = the chunk holds ≥1 voxel, else 0), as a small R8 3D
// texture for the raymarch DDA to skip empty chunks INSIDE the model's bbox. One
// texel per 16³ chunk (so ~8³ for a 128³ canvas) — cheap to rebuild every edit
// from chunk.count. Aligned to grid chunks; the shader maps a volume cell to a
// chunk via (cell + volumeOrigin)/16 - chunkOrigin. texCw pads the row to a
// multiple of 4 (R8 3D-texture alignment, like the volume's texW).
export interface CoarseData {
  data: Uint8Array;
  cw: number; ch: number; cd: number;
  texCw: number;
  cox: number; coy: number; coz: number; // min chunk coords
}

export function buildCoarseData(grid: VoxelGrid, bounds: CellBounds, into?: Uint8Array): CoarseData {
  const cox = Math.floor(bounds.minIx / CHUNK_SIZE);
  const coy = Math.floor(bounds.minIy / CHUNK_SIZE);
  const coz = Math.floor(bounds.minIz / CHUNK_SIZE);
  const cw = Math.max(1, Math.floor((bounds.maxIx - 1) / CHUNK_SIZE) - cox + 1);
  const ch = Math.max(1, Math.floor((bounds.maxIy - 1) / CHUNK_SIZE) - coy + 1);
  const cd = Math.max(1, Math.floor((bounds.maxIz - 1) / CHUNK_SIZE) - coz + 1);
  const texCw = (cw + 3) & ~3;
  const n = texCw * ch * cd;
  const data = into && into.length >= n ? into : new Uint8Array(n);
  data.fill(0);
  for (let cz = 0; cz < cd; cz++)
    for (let cy = 0; cy < ch; cy++)
      for (let cx = 0; cx < cw; cx++) {
        const chunk = grid.getChunkStorage(cox + cx, coy + cy, coz + cz);
        if (chunk && chunk.count > 0) data[cx + texCw * (cy + ch * cz)] = 255;
      }
  return { data, cw, ch, cd, texCw, cox, coy, coz };
}

// The palette as a 256x1 RGBA byte buffer (index 0 = empty → alpha 0). The shader
// samples this with the hit voxel's palette index to colour it.
export function buildPaletteData(palette: PaletteLookup, into?: Uint8Array): Uint8Array {
  const out = into && into.length >= 256 * 4 ? into : new Uint8Array(256 * 4);
  out[0] = out[1] = out[2] = out[3] = 0; // 0 = empty
  for (let p = 1; p < 256; p++) {
    const c = palette.color(p);
    const o = p * 4;
    out[o] = Math.round(c.r * 255);
    out[o + 1] = Math.round(c.g * 255);
    out[o + 2] = Math.round(c.b * 255);
    out[o + 3] = 255;
  }
  return out;
}
